import { useCallback, useSyncExternalStore } from 'react';
import { Box, Stack } from '@mui/material';
import { TestIds } from '../../constants';
import { drawerSlotStore, type DrawerSlotRecord } from '../../services/drawerSlots/drawerSlotStore';

/**
 * In-flow region that hosts every registered drawer slot side-by-side, to the
 * right of the main content. Each slot collapses to zero width when closed, so
 * `<main>` reclaims the space; open slots keep their content's natural width
 * (`flexShrink: 0`) and push content aside. Slots render in store order:
 * managed (shell) drawers left, module drawers right.
 */
export function DrawerSlotRegion() {
  const slots = useSyncExternalStore(drawerSlotStore.subscribe, drawerSlotStore.getSnapshot);

  return (
    <Stack direction="row" data-testid={TestIds.DRAWER_SLOT_REGION} sx={{ height: '100%' }}>
      {slots.map((slot) => (
        <DrawerSlotMount key={slot.id} slot={slot} />
      ))}
    </Stack>
  );
}

function DrawerSlotMount({ slot }: { slot: DrawerSlotRecord }) {
  const mountRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && !node.contains(slot.element)) {
        // The slot element is the single grid item. min-width:0 + overflow
        // hidden let the `1fr`↔`0fr` track collapse below the content's
        // intrinsic width, which is what makes the open/close slide animate.
        slot.element.style.minWidth = '0';
        slot.element.style.overflow = 'hidden';
        slot.element.style.height = '100%';
        node.appendChild(slot.element);
      }
    },
    [slot.element]
  );

  return (
    <Box
      ref={mountRef}
      data-drawer-slot-open={slot.open}
      sx={{
        // Animate open/close as a smooth slide. Width can't transition to/from
        // `auto`, so the slot is a 1-column grid whose track animates between
        // `0fr` (closed) and `1fr` (open) — interpolating to the content's
        // natural width with no hardcoded size (drawers vary; OG Assist is even
        // resizable). The grid item (slot.element) has min-width:0 + overflow
        // hidden so the track can collapse below content width.
        display: 'grid',
        gridTemplateColumns: slot.open ? '1fr' : '0fr',
        transition: 'grid-template-columns 220ms ease-in-out',
        // Respect users who request reduced motion (WCAG 2.3.3 / Capital DS):
        // snap open/closed instead of sliding.
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
        flexShrink: 0,
        overflow: 'hidden',
        // Make each slot a containing block for `position: fixed` descendants.
        // A module may portal in a fixed-position drawer (e.g. OG Assist's MUI
        // persistent Drawer paper is `position: fixed; right: 0`); without this
        // it would pin to the viewport edge and overlap neighboring drawers
        // instead of staying within its slot. A transform establishes the
        // containing block generically — no knowledge of the drawer's markup.
        // It also re-anchors the paper's `top: 0` to the slot, which already
        // sits below the header.
        transform: 'translateZ(0)',
        // The slot already sits below the header (it lives in LayoutContent),
        // so cancel any header-offset paddingTop a portaled MUI Drawer applies
        // for its own (viewport-fixed) navbar — otherwise it adds a gap at the
        // top of the drawer. Doubled class wins specificity over the SDK's rule;
        // no-op for in-flow panels with no `.MuiDrawer-paper`.
        '& .MuiDrawer-paper.MuiDrawer-paper': {
          paddingTop: 0,
        },
      }}
    />
  );
}
